;(() => {
  class MarqueeLoop extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]')
      this.group = this.querySelector('[data-marquee-group]')
      if (!this.track || !this.group) return

      this.originalItems = Array.from(this.group.children)
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.handleMotionChange = () => this.queueBuild()
      this.motionQuery.addEventListener('change', this.handleMotionChange)
      this.resizeObserver = new ResizeObserver(() => this.queueBuild())
      this.resizeObserver.observe(this)
      this.handleMediaLoad = () => this.queueBuild()
      window.addEventListener('load', this.handleMediaLoad, { once: true })
      document.fonts?.ready.then(() => this.queueBuild())
      this.queueBuild()
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect()
      this.motionQuery?.removeEventListener('change', this.handleMotionChange)
      window.removeEventListener('load', this.handleMediaLoad)
      window.cancelAnimationFrame(this.buildFrame)
    }

    queueBuild() {
      if (!this.isConnected) return
      window.cancelAnimationFrame(this.buildFrame)
      this.buildFrame = window.requestAnimationFrame(() => this.build())
    }

    build() {
      if (!this.isConnected || !this.offsetWidth) return

      this.classList.remove('is-ready')
      this.classList.remove('is-measuring')
      this.track.querySelectorAll('[data-marquee-copy]').forEach((copy) => copy.remove())
      this.group.querySelectorAll('[data-marquee-filler]').forEach((copy) => copy.remove())

      if (!this.originalItems.length || this.motionQuery.matches) return

      this.classList.add('is-measuring')
      const originalWidth = this.group.getBoundingClientRect().width
      if (!originalWidth) {
        this.classList.remove('is-measuring')
        return
      }

      const minimumGroupWidth = this.offsetWidth + originalWidth
      const maximumCopies = Math.ceil(minimumGroupWidth / originalWidth) + 1
      let copyCount = 0
      while (this.group.scrollWidth < minimumGroupWidth && copyCount < maximumCopies) {
        this.originalItems.forEach((item) => {
          const copy = this.createHiddenCopy(item)
          copy.dataset.marqueeFiller = ''
          this.group.append(copy)
        })
        copyCount += 1
      }

      const duplicate = this.group.cloneNode(true)
      duplicate.dataset.marqueeCopy = ''
      duplicate.setAttribute('aria-hidden', 'true')
      duplicate.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block')
      })
      duplicate.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1')
      })
      this.track.append(duplicate)

      this.group.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', this.handleMediaLoad, { once: true })
      })

      this.style.setProperty('--marquee-distance', `${this.group.getBoundingClientRect().width}px`)
      const speed = Number(this.dataset.speed) || 200
      const duration = Math.max(this.group.getBoundingClientRect().width / speed, 4)
      this.style.setProperty('--marquee-duration', `${duration}s`)
      this.classList.remove('is-measuring')
      this.classList.add('is-ready')
    }

    createHiddenCopy(item) {
      const copy = item.cloneNode(true)
      copy.setAttribute('aria-hidden', 'true')
      copy.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block')
      })
      copy.removeAttribute('data-shopify-editor-block')

      const interactiveElements = copy.matches('a, button, input, select, textarea, [tabindex]')
        ? [copy, ...copy.querySelectorAll('a, button, input, select, textarea, [tabindex]')]
        : copy.querySelectorAll('a, button, input, select, textarea, [tabindex]')

      interactiveElements.forEach((element) => {
        element.setAttribute('tabindex', '-1')
        element.removeAttribute('data-shopify-editor-block')
      })
      return copy
    }
  }

  if (!customElements.get('marquee-loop')) {
    customElements.define('marquee-loop', MarqueeLoop)
  }
})()
